import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Car,
  Gauge,
  Cog,
  Fuel,
  Users,
  DoorOpen,
  Shield,
  Eye,
  Factory,
  Lightbulb,
  Search,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Radio,
  Download,
  Loader2,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PdfReferenceNoteModal from '../components/PdfReferenceNoteModal';
import { useAuth } from '../context/AuthContext';
import { validateVIN, generateVinDecodePdf, fetchVinDecodePricing } from '../services/vinService';
import { VINDecodeResult } from '../services/vinService';
import { getErrorMessage } from '../services/apiService';
import { getDashboardSettings } from '../services/settingsService';
import StatCard from '../components/vin-decode/StatCard';
import Widget from '../components/vin-decode/Widget';
import FeatureItem from '../components/vin-decode/FeatureItem';
import ProgressBar from '../components/vin-decode/ProgressBar';

// Session storage keys for persisting search results
const VINDECODE_SESSION_KEYS = {
  VIN: 'vindecode_search_vin',
  VEHICLE_DATA: 'vindecode_search_vehicle_data',
  REPORT_ID: 'vindecode_search_report_id',
  FETCHED_AT: 'vindecode_search_fetched_at',
};

// Helper functions for session storage
const loadFromSession = <T,>(key: string): T | null => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveToSession = (key: string, value: unknown): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Session storage might be full or disabled
  }
};

const clearVINDecodeSession = (): void => {
  Object.values(VINDECODE_SESSION_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

const VINDecode: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Redirect if user doesn't have VIN Decoder access
  useEffect(() => {
    if (isLoading) return;
    if (user?.canAccessVinDecoder === false) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  // Initialize state from sessionStorage if available
  const [vin, setVin] = useState(() => loadFromSession<string>(VINDECODE_SESSION_KEYS.VIN) || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicleData, setVehicleData] = useState<VINDecodeResult | null>(() =>
    loadFromSession<VINDecodeResult>(VINDECODE_SESSION_KEYS.VEHICLE_DATA)
  );
  // Report metadata (from API response) - also persisted to sessionStorage
  const [reportId, setReportId] = useState<string | null>(() =>
    loadFromSession<string>(VINDECODE_SESSION_KEYS.REPORT_ID)
  );
  const [fetchedAt, setFetchedAt] = useState<string | null>(() =>
    loadFromSession<string>(VINDECODE_SESSION_KEYS.FETCHED_AT)
  );
  // Pricing state
  const [showPricing, setShowPricing] = useState(true);
  const [vinDecodeCostPerVin, setVinDecodeCostPerVin] = useState<number | null>(null);
  // PDF generation state
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Persist state changes to sessionStorage
  useEffect(() => {
    saveToSession(VINDECODE_SESSION_KEYS.VIN, vin);
  }, [vin]);

  useEffect(() => {
    if (vehicleData) {
      saveToSession(VINDECODE_SESSION_KEYS.VEHICLE_DATA, vehicleData);
    }
  }, [vehicleData]);

  useEffect(() => {
    if (reportId) {
      saveToSession(VINDECODE_SESSION_KEYS.REPORT_ID, reportId);
    }
  }, [reportId]);

  useEffect(() => {
    if (fetchedAt) {
      saveToSession(VINDECODE_SESSION_KEYS.FETCHED_AT, fetchedAt);
    }
  }, [fetchedAt]);

  // Fetch dashboard settings
  useEffect(() => {
    const fetchSettings = async () => {
      const settings = await getDashboardSettings();
      setShowPricing(settings.showPricing);
    };
    fetchSettings();
  }, []);

  // Fetch VIN Decode pricing on mount
  useEffect(() => {
    let cancelled = false;
    const loadPricing = async () => {
      try {
        const pricing = await fetchVinDecodePricing();
        if (!cancelled) setVinDecodeCostPerVin(pricing.costPerVin);
      } catch {
        if (!cancelled) setVinDecodeCostPerVin(null);
      }
    };
    loadPricing();
    return () => { cancelled = true; };
  }, []);

  // Body scroll lock when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  // Core search function that takes a VIN parameter
  const performSearch = useCallback(async (searchVin: string) => {
    if (!searchVin || searchVin.length < 5) {
      setError('Please enter a valid VIN (minimum 5 characters)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await validateVIN(searchVin);
      if (response.isValid && response.decodeResult) {
        setVehicleData(response.decodeResult);
        // Save report metadata (only available for authenticated users)
        setReportId(response.reportId || null);
        setFetchedAt(response.fetchedAt || null);
      } else {
        setError(response.errorMessage || 'Failed to decode VIN');
        setVehicleData(null);
        setReportId(null);
        setFetchedAt(null);
      }
    } catch (err: unknown) {
      // Surface the backend's actual reason (getErrorMessage parses the API error body).
      setError(getErrorMessage(err));
      setVehicleData(null);
      setReportId(null);
      setFetchedAt(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Wrapper for button click that uses current vin state
  const handleSearch = () => {
    performSearch(vin);
  };

  // Handle VIN from URL query parameter (e.g., from "Full Decode" link on NMVTIS page)
  useEffect(() => {
    const vinParam = searchParams.get('vin');
    if (vinParam && vinParam.length >= 5) {
      const upperVin = vinParam.replace(/\s/g, '').toUpperCase();
      setVin(upperVin);
      // Auto-search the VIN
      performSearch(upperVin);
    }
  }, [searchParams, performSearch]);

  const handleClear = () => {
    setVin('');
    setVehicleData(null);
    setReportId(null);
    setFetchedAt(null);
    setError(null);
    // Clear sessionStorage when user explicitly clears
    clearVINDecodeSession();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleGeneratePdf = async (referenceNote: string) => {
    if (!vin || !vehicleData) {
      setPdfError('No VIN data available for PDF generation');
      setShowPdfModal(false);
      return;
    }

    setPdfError(null);
    setIsGeneratingPdf(true);

    try {
      const requestedBy = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.email || 'Unknown User';

      await generateVinDecodePdf({
        vin,
        requestedBy,
        referenceNote: referenceNote.trim() || undefined,
        reportId: reportId || undefined,
      });

      // Close modal on success
      setShowPdfModal(false);
    } catch (err: any) {
      setPdfError(err.message || 'Failed to generate PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Safety feature label mapping
  const safetyLabels: Record<string, string> = {
    abs: 'Anti-lock Braking (ABS)',
    esc: 'Stability Control (ESC)',
    tractionControl: 'Traction Control',
    tpms: 'TPMS',
    autoReverse: 'Auto-Reverse Windows',
    edr: 'Event Data Recorder',
    keylessIgnition: 'Keyless Ignition',
  };

  // Driver assist feature label mapping
  const assistLabels: Record<string, string> = {
    acc: 'Adaptive Cruise Control',
    cib: 'Crash Imminent Braking',
    fcw: 'Forward Collision Warning',
    dbs: 'Dynamic Brake Support',
    bsw: 'Blind Spot Warning',
    ldw: 'Lane Departure Warning',
    lka: 'Lane Keeping Assist',
    backupCamera: 'Backup Camera',
    parkingAssist: 'Parking Assist',
    rearCrossTraffic: 'Rear Cross Traffic Alert',
    paeb: 'Pedestrian AEB',
  };

  // Helper to check if a feature value indicates it's present
  // Counts any non-empty value (e.g., "Standard", "Direct", "Indirect", etc.)
  const hasFeatureValue = (value: string | undefined | null): boolean => {
    return !!value && value.trim().length > 0;
  };

  // Calculate feature coverage with missing items
  const calculateCoverage = () => {
    if (!vehicleData) return { safety: 0, safetyTotal: 0, assist: 0, assistTotal: 0, safetyMissing: [], assistMissing: [] };

    const safetyFeatures = vehicleData.safety || {};
    const assistFeatures = vehicleData.driverAssist || {};

    const safetyMissing: string[] = [];
    const assistMissing: string[] = [];

    // Count safety features and track missing
    let safetyCount = 0;
    Object.entries(safetyLabels).forEach(([key, label]) => {
      const value = safetyFeatures[key as keyof typeof safetyFeatures];
      if (hasFeatureValue(value)) {
        safetyCount++;
      } else {
        safetyMissing.push(label);
      }
    });

    // Count assist features and track missing
    let assistCount = 0;
    Object.entries(assistLabels).forEach(([key, label]) => {
      const value = assistFeatures[key as keyof typeof assistFeatures];
      if (hasFeatureValue(value)) {
        assistCount++;
      } else {
        assistMissing.push(label);
      }
    });

    return {
      safety: safetyCount,
      safetyTotal: Object.keys(safetyLabels).length,
      assist: assistCount,
      assistTotal: Object.keys(assistLabels).length,
      safetyMissing,
      assistMissing,
    };
  };

  const countAirbags = () => {
    const airbagLabels = ['Front', 'Side', 'Curtain', 'Knee'];
    if (!vehicleData?.airbags) return { count: 0, total: 4, missing: airbagLabels };
    const { front, side, curtain, knee } = vehicleData.airbags;
    const missing: string[] = [];
    if (!front) missing.push('Front');
    if (!side) missing.push('Side');
    if (!curtain) missing.push('Curtain');
    if (!knee) missing.push('Knee');
    const count = 4 - missing.length;
    return { count, total: 4, missing };
  };

  const countLighting = () => {
    const lightingLabels = { drl: 'Daytime Running Lights', headlampSource: 'Headlamp Source', autoBeam: 'Auto High Beam' };
    if (!vehicleData?.lighting) return { count: 0, total: 3, missing: Object.values(lightingLabels) };
    const { drl, headlampSource, autoBeam } = vehicleData.lighting;
    const missing: string[] = [];
    if (!drl) missing.push('Daytime Running Lights');
    if (!headlampSource) missing.push('Headlamp Source');
    if (!autoBeam) missing.push('Auto High Beam');
    const count = 3 - missing.length;
    return { count, total: 3, missing };
  };

  const coverage = calculateCoverage();
  const airbagCoverage = countAirbags();
  const lightingCoverage = countLighting();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="relative z-10 lg:ml-[220px] pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {/* Page Header */}
          <div className="glass-card-strong p-4 md:p-6 rounded-xl mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1 md:mb-2">
                  <span className="text-[#c41e3a] text-xs font-bold uppercase tracking-wider">VIN Intelligence Report</span>
                </div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-[#0f172a] mb-1">
                  VIN Decoder
                </h1>
                <p className="text-[#64748b] text-xs md:text-sm">Decode vehicle information and specifications</p>
              </div>
              {vehicleData && reportId && (
                <div className="flex flex-wrap items-center gap-3 md:gap-4 glass-card px-3 md:px-4 py-2 md:py-3 rounded-lg">
                  <div className="text-left md:text-right">
                    <div className="text-[10px] text-[#64748b] uppercase tracking-wider">Report ID</div>
                    <div className="font-mono text-sm font-semibold text-[#0f172a]">RPT-{reportId.substring(0, 6).toUpperCase()}</div>
                  </div>
                  {fetchedAt && (
                    <>
                      <div className="hidden md:block w-px h-8 bg-black/10"></div>
                      <div className="text-left md:text-right">
                        <div className="text-[10px] text-[#64748b] uppercase tracking-wider">Generated</div>
                        <div className="text-sm font-medium text-[#0f172a]">{new Date(fetchedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                      </div>
                    </>
                  )}
                  <div className="hidden md:block w-px h-8 bg-black/10"></div>
                  <div className="flex items-center gap-1.5 text-[#16a34a]">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-sm font-semibold">Verified</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div data-tour="vin-decode" className="glass-card-strong p-3 md:p-4 rounded-xl mb-6">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex-1 flex items-center gap-3 bg-white rounded-lg px-3 md:px-4 py-2.5 md:py-3 border border-black/[0.08]">
                <Search className="w-5 h-5 text-[#94a3b8] flex-shrink-0" />
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => setVin(e.target.value.replace(/\s/g, '').toUpperCase())}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter VIN to decode"
                  className="flex-1 bg-transparent text-[#0f172a] font-mono text-sm tracking-wider placeholder-[#94a3b8] focus:outline-none min-w-0"
                />
                {loading && (
                  <Loader2 className="w-5 h-5 text-[#c41e3a] animate-spin flex-shrink-0" />
                )}
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                {showPricing && vinDecodeCostPerVin !== null && (
                  <span className="badge-success px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap">
                    {vinDecodeCostPerVin === 0
                      ? 'No Charge'
                      : `Est. $${vinDecodeCostPerVin.toFixed(2)}`}
                  </span>
                )}
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="btn-primary flex-1 sm:flex-none px-4 md:px-6 py-2.5 md:py-3 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                >
                  {loading ? 'Searching...' : 'Search VIN'}
                </button>
                <button
                  onClick={handleClear}
                  className="btn-secondary px-4 md:px-5 py-2.5 md:py-3 text-sm md:text-base"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-xl text-[#dc2626] text-sm">
              {error}
            </div>
          )}

          {/* VIN Validation Card */}
          {vehicleData && (
            <div className="mb-6 glass-card p-4 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#c41e3a]/10 flex items-center justify-center">
                  <Car className="w-5 h-5 text-[#c41e3a]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-[#0f172a]">
                      {vehicleData.modelYear} {vehicleData.make} {vehicleData.model}
                    </p>
                    <div className="flex items-center gap-3">
                      {vehicleData.checkDigitValid && (
                        <span className="badge-success px-2 py-1 rounded text-xs font-medium">Check Digit Valid</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-[#64748b]">
                    <span>VIN: <span className="font-mono text-[#c41e3a]">{vin}</span></span>
                    {vehicleData.bodyClass && <span>| {vehicleData.bodyClass}</span>}
                    {vehicleData.driveType && <span>| {vehicleData.driveType}</span>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

          {/* Dashboard Content */}
          {vehicleData && (
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              {/* Top Stats Row */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6">
                <StatCard
                  icon={Gauge}
                  label="Engine"
                  value={
                    vehicleData.engine?.displacementL
                      ? `${vehicleData.engine.displacementL}L`
                      : '—'
                  }
                  subvalue={vehicleData.engine?.configuration}
                />
                <StatCard
                  icon={Cog}
                  label="Transmission"
                  value={
                    vehicleData.transmission?.speeds
                      ? `${vehicleData.transmission.speeds}-Spd`
                      : '—'
                  }
                  subvalue={vehicleData.transmission?.style}
                />
                <StatCard
                  icon={Fuel}
                  label="Fuel"
                  value={vehicleData.engine?.fuelType || '—'}
                />
                <StatCard
                  icon={Users}
                  label="Seating"
                  value={vehicleData.seats ? `${vehicleData.seats} Pass` : '—'}
                  subvalue={vehicleData.seatRows ? `${vehicleData.seatRows} Rows` : undefined}
                />
                <StatCard icon={DoorOpen} label="Doors" value={vehicleData.doors || '—'} />
                <StatCard
                  icon={Car}
                  label="Body"
                  value={vehicleData.bodyClass || '—'}
                  subvalue={vehicleData.vehicleType}
                />
              </div>

              {/* Main Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
                {/* Engine Details */}
                <Widget title="Engine Specifications" icon={Gauge}>
                  <div className="space-y-3">
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Model</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.engine?.model || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Configuration</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.engine?.configuration || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Displacement</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.engine?.displacementL
                          ? `${vehicleData.engine.displacementL}L (${vehicleData.engine.displacementCC} CC)`
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Cylinders</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.engine?.cylinders || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Details</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right break-words min-w-0">
                        {vehicleData.engine?.otherInfo || '—'}
                      </span>
                    </div>
                  </div>
                </Widget>

                {/* Drivetrain */}
                <Widget title="Drivetrain" icon={Cog}>
                  <div className="space-y-3">
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Transmission</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.transmission?.style || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Speeds</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.transmission?.speeds || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Drive Type</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.driveType || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Brake System</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.brakeSystem || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">GVWR</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.gvwr || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Curb Weight</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.curbWeight ? `${vehicleData.curbWeight} lbs` : '—'}
                      </span>
                    </div>
                  </div>
                </Widget>

                {/* Manufacturing */}
                <Widget title="Manufacturing" icon={Factory}>
                  <div className="space-y-3">
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Manufacturer</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.manufacturerName || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">NCIC Make Code</span>
                      <span className="text-[#0f172a] font-medium text-sm font-mono text-right truncate">
                        {vehicleData.ncicMakeCode || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Plant City</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.plant?.city || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2 border-b border-black/[0.06]">
                      <span className="text-[#64748b] text-sm flex-shrink-0">State</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.plant?.state || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-2 py-2">
                      <span className="text-[#64748b] text-sm flex-shrink-0">Country</span>
                      <span className="text-[#0f172a] font-medium text-sm text-right truncate">
                        {vehicleData.plant?.country || '—'}
                      </span>
                    </div>
                  </div>
                </Widget>
              </div>

              {/* Feature Coverage Section */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
                <div className="glass-card rounded-xl border border-black/[0.08] p-4 md:p-5">
                  <h3 className="text-[#0f172a] font-semibold mb-4">Feature Coverage</h3>
                  <ProgressBar
                    current={coverage.safety}
                    total={coverage.safetyTotal}
                    label="Safety Systems"
                    missingItems={coverage.safetyMissing}
                  />
                  <ProgressBar
                    current={coverage.assist}
                    total={coverage.assistTotal}
                    label="Driver Assist"
                    missingItems={coverage.assistMissing}
                  />
                  <ProgressBar
                    current={airbagCoverage.count}
                    total={airbagCoverage.total}
                    label="Airbag Zones"
                    missingItems={airbagCoverage.missing}
                  />
                  <ProgressBar
                    current={lightingCoverage.count}
                    total={lightingCoverage.total}
                    label="Lighting"
                    missingItems={lightingCoverage.missing}
                  />

                  <div className="mt-5 pt-4 border-t border-black/[0.08]">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-2 h-2 rounded-full bg-[#16a34a]" />
                      <span className="text-[#64748b] text-xs">Standard</span>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-2 h-2 rounded-full bg-[#d97706]" />
                      <span className="text-[#64748b] text-xs">Optional</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#94a3b8]" />
                      <span className="text-[#64748b] text-xs">Not Available</span>
                    </div>
                  </div>
                </div>

                {/* Safety Systems */}
                <Widget title="Safety Systems" icon={Shield}>
                  <div className="space-y-1">
                    <FeatureItem
                      label="Anti-lock Braking (ABS)"
                      status={vehicleData.safety?.abs}
                    />
                    <FeatureItem
                      label="Stability Control (ESC)"
                      status={vehicleData.safety?.esc}
                    />
                    <FeatureItem
                      label="Traction Control"
                      status={vehicleData.safety?.tractionControl}
                    />
                    <FeatureItem
                      label="TPMS"
                      status={vehicleData.safety?.tpms ? 'Standard' : null}
                    />
                    <FeatureItem
                      label="Auto-Reverse Windows"
                      status={vehicleData.safety?.autoReverse}
                    />
                    <FeatureItem
                      label="Event Data Recorder"
                      status={vehicleData.safety?.edr}
                    />
                    <FeatureItem
                      label="Keyless Ignition"
                      status={vehicleData.safety?.keylessIgnition}
                    />
                  </div>
                </Widget>

                {/* Driver Assist - Column 1 */}
                <Widget title="Collision Avoidance" icon={Eye}>
                  <div className="space-y-1">
                    <FeatureItem
                      label="Adaptive Cruise Control"
                      status={vehicleData.driverAssist?.acc}
                    />
                    <FeatureItem
                      label="Crash Imminent Braking"
                      status={vehicleData.driverAssist?.cib}
                    />
                    <FeatureItem
                      label="Forward Collision Warning"
                      status={vehicleData.driverAssist?.fcw}
                    />
                    <FeatureItem
                      label="Dynamic Brake Support"
                      status={vehicleData.driverAssist?.dbs}
                    />
                    <FeatureItem
                      label="Pedestrian AEB"
                      status={vehicleData.driverAssist?.paeb}
                    />
                    <FeatureItem
                      label="Rear Cross Traffic Alert"
                      status={vehicleData.driverAssist?.rearCrossTraffic}
                    />
                  </div>
                </Widget>

                {/* Driver Assist - Column 2 */}
                <Widget title="Lane & Parking" icon={Radio}>
                  <div className="space-y-1">
                    <FeatureItem
                      label="Blind Spot Warning"
                      status={vehicleData.driverAssist?.bsw}
                    />
                    <FeatureItem
                      label="Lane Departure Warning"
                      status={vehicleData.driverAssist?.ldw}
                    />
                    <FeatureItem
                      label="Lane Keeping Assist"
                      status={vehicleData.driverAssist?.lka}
                    />
                    <FeatureItem
                      label="Backup Camera"
                      status={vehicleData.driverAssist?.backupCamera}
                    />
                    <FeatureItem
                      label="Parking Assist"
                      status={vehicleData.driverAssist?.parkingAssist}
                    />
                  </div>
                </Widget>
              </div>

              {/* Bottom Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Airbags */}
                <Widget title="Airbag Configuration" icon={Shield}>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/40 rounded-lg p-4 border border-black/[0.08]">
                      <div className="flex items-center gap-2 mb-2">
                        {vehicleData.airbags?.front ? (
                          <CheckCircle className="w-4 h-4 text-[#16a34a]" />
                        ) : (
                          <XCircle className="w-4 h-4 text-[#94a3b8]" />
                        )}
                        <span
                          className={`font-medium text-sm ${
                            vehicleData.airbags?.front ? 'text-[#16a34a]' : 'text-[#94a3b8]'
                          }`}
                        >
                          Front
                        </span>
                      </div>
                      <p className="text-[#64748b] text-xs">
                        {vehicleData.airbags?.front || 'Not equipped'}
                      </p>
                    </div>
                    <div className="bg-white/40 rounded-lg p-4 border border-black/[0.08]">
                      <div className="flex items-center gap-2 mb-2">
                        {vehicleData.airbags?.side ? (
                          <CheckCircle className="w-4 h-4 text-[#16a34a]" />
                        ) : (
                          <XCircle className="w-4 h-4 text-[#94a3b8]" />
                        )}
                        <span
                          className={`font-medium text-sm ${
                            vehicleData.airbags?.side ? 'text-[#16a34a]' : 'text-[#94a3b8]'
                          }`}
                        >
                          Side
                        </span>
                      </div>
                      <p className="text-[#64748b] text-xs">
                        {vehicleData.airbags?.side || 'Not equipped'}
                      </p>
                    </div>
                    <div className="bg-white/40 rounded-lg p-4 border border-black/[0.08]">
                      <div className="flex items-center gap-2 mb-2">
                        {vehicleData.airbags?.curtain ? (
                          <CheckCircle className="w-4 h-4 text-[#16a34a]" />
                        ) : (
                          <XCircle className="w-4 h-4 text-[#94a3b8]" />
                        )}
                        <span
                          className={`font-medium text-sm ${
                            vehicleData.airbags?.curtain ? 'text-[#16a34a]' : 'text-[#94a3b8]'
                          }`}
                        >
                          Curtain
                        </span>
                      </div>
                      <p className="text-[#64748b] text-xs">
                        {vehicleData.airbags?.curtain || 'Not equipped'}
                      </p>
                    </div>
                    <div className="bg-white/40 rounded-lg p-4 border border-black/[0.08]">
                      <div className="flex items-center gap-2 mb-2">
                        {vehicleData.airbags?.knee ? (
                          <CheckCircle className="w-4 h-4 text-[#16a34a]" />
                        ) : (
                          <XCircle className="w-4 h-4 text-[#94a3b8]" />
                        )}
                        <span
                          className={`font-medium text-sm ${
                            vehicleData.airbags?.knee ? 'text-[#16a34a]' : 'text-[#94a3b8]'
                          }`}
                        >
                          Knee
                        </span>
                      </div>
                      <p className="text-[#64748b] text-xs">
                        {vehicleData.airbags?.knee || 'Not equipped'}
                      </p>
                    </div>
                  </div>
                </Widget>

                {/* Lighting */}
                <Widget title="Lighting Systems" icon={Lightbulb}>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white/40 rounded-lg p-4 border border-black/[0.08] text-center">
                      {vehicleData.lighting?.drl ? (
                        <CheckCircle className="w-6 h-6 text-[#16a34a] mx-auto mb-2" />
                      ) : (
                        <XCircle className="w-6 h-6 text-[#94a3b8] mx-auto mb-2" />
                      )}
                      <div className="text-[#0f172a] font-medium text-sm mb-1">DRL</div>
                      <div className="text-[#64748b] text-xs">Daytime Running</div>
                    </div>
                    <div className="bg-white/40 rounded-lg p-4 border border-black/[0.08] text-center">
                      {vehicleData.lighting?.headlampSource ? (
                        <CheckCircle className="w-6 h-6 text-[#16a34a] mx-auto mb-2" />
                      ) : (
                        <XCircle className="w-6 h-6 text-[#94a3b8] mx-auto mb-2" />
                      )}
                      <div className="text-[#0f172a] font-medium text-sm mb-1">
                        {vehicleData.lighting?.headlampSource || 'N/A'}
                      </div>
                      <div className="text-[#64748b] text-xs">Headlamp Source</div>
                    </div>
                    <div className="bg-white/40 rounded-lg p-4 border border-black/[0.08] text-center">
                      {vehicleData.lighting?.autoBeam ? (
                        <CheckCircle className="w-6 h-6 text-[#16a34a] mx-auto mb-2" />
                      ) : (
                        <XCircle className="w-6 h-6 text-[#94a3b8] mx-auto mb-2" />
                      )}
                      <div className="text-[#0f172a] font-medium text-sm mb-1">Auto Beam</div>
                      <div className="text-[#64748b] text-xs">Adaptive Switching</div>
                    </div>
                  </div>
                </Widget>
              </div>

            </div>
          )}

          {/* Empty State */}
          {!vehicleData && !loading && (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Car className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
                <h2 className="text-xl text-[#475569] mb-2">No Vehicle Data</h2>
                <p className="text-[#64748b]">Enter a VIN above to decode vehicle information</p>
              </div>
            </div>
          )}
        </main>
        <Footer />

      {/* PDF Error Toast */}
      {pdfError && (
        <div className="fixed bottom-24 right-8 z-50 max-w-sm p-4 bg-[#dc2626]/10 border border-[#dc2626]/30 rounded-lg text-[#dc2626] text-sm shadow-lg">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">PDF Generation Failed</p>
              <p className="text-[#b91c1c] text-xs mt-1">{pdfError}</p>
            </div>
            <button
              onClick={() => setPdfError(null)}
              className="text-[#dc2626] hover:text-[#b91c1c] ml-auto"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Floating PDF Button */}
      {vehicleData && (
        <button
          onClick={() => setShowPdfModal(true)}
          disabled={isGeneratingPdf}
          className="fixed bottom-8 right-8 px-6 py-4 bg-[#c41e3a] hover:bg-[#a01729] text-white font-semibold rounded-full shadow-2xl transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed z-50 hover:scale-105"
          title="Generate PDF Report"
        >
          {isGeneratingPdf ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              <span>Generate PDF</span>
            </>
          )}
        </button>
      )}

      {/* PDF Reference Note Modal */}
      <PdfReferenceNoteModal
        isOpen={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        onGenerate={handleGeneratePdf}
        isGenerating={isGeneratingPdf}
      />
    </div>
  );
};

export default VINDecode;
